// Demo-mode helpers: sign in as the seeded owner, build an empty business, QA scenarios, clock jumps.
import { services } from '../adapters';
import { t } from '../i18n';
import { HOUR, addDaysToKey, atMinuteOfDay, dayKeyOf, toIso, toMs, DAY } from '../domain/time';
import { newId, type Appointment } from '../domain/types';

/** Signs in as the (first) owner found in storage via the mock OTP. Returns false when no owner exists. */
let entering: Promise<boolean> | null = null;
/** Resolves when a sign-in started by the demo hub has finished (the owner app waits for it before deciding to show onboarding). */
export const demoEntering = (): Promise<boolean> | null => entering;
export function enterDemoOwner(): Promise<boolean> {
  const p = enterDemoOwnerNow();
  entering = p;
  void p.finally(() => { if (entering === p) entering = null; });
  return p;
}
async function enterDemoOwnerNow(): Promise<boolean> {
  await services.ready;
  const existing = services.auth.getSession();
  if (existing?.businessId) {
    const biz = await services.storage.repo('businesses').get(existing.businessId);
    if (biz) return true;
  }
  const owners = await services.storage.repo('owners').list();
  const businesses = await services.storage.repo('businesses').list();
  const demoIds = new Set(businesses.filter((b) => b.isDemo).map((b) => b.id));
  const owner = owners.find((o) => demoIds.has(o.businessId)) ?? owners[0];
  if (!owner) return false;
  const req = await services.auth.requestOtp(owner.phone);
  if (!req.ok) return false;
  const code = services.auth.getDemoCode(owner.phone);
  if (!code) return false;
  const v = await services.auth.verifyOtp(owner.phone, code);
  if (!v.ok) return false;
  const r = await services.app.restoreOwner();
  return r.ok;
}

/** QA: wipe everything, then create a fresh business with presets but no clients and no appointments. */
export async function createEmptyBusiness(): Promise<boolean> {
  await services.demo.resetEmpty();
  const phone = '0521234567';
  const req = await services.auth.requestOtp(phone);
  if (!req.ok) return false;
  const code = services.auth.getDemoCode(phone);
  if (!code) return false;
  const v = await services.auth.verifyOtp(phone, code);
  if (!v.ok) return false;
  const made = await services.app.createOwnerAccount({ phone, businessName: t('owner.x.emptyBusinessName'), profession: 'nails' });
  return made.ok;
}

/** QA: fully book the first day (today when it still has free slots) for the first service so the waitlist CTA appears. */
export async function seedFullDay(): Promise<number> {
  await services.ready;
  const [business] = await services.storage.repo('businesses').list();
  if (!business) return 0;
  const services_ = (await services.storage.repo('services').list({ where: { businessId: business.id } })).filter((s) => s.active).sort((a, b) => a.sortOrder - b.sortOrder);
  const service = services_[0];
  const [client] = await services.storage.repo('clients').list({ where: { businessId: business.id } });
  if (!service || !client) return 0;
  const now = services.clock.now();
  const today = dayKeyOf(now, business.tz);
  let booked = 0;
  for (let i = 0; i < 14; i++) {
    const day = addDaysToKey(today, i);
    const res = await services.app.getSlots({ businessId: business.id, serviceId: service.id, staffId: 'any', fromDay: day, toDay: day });
    if (!res.ok) continue;
    const slots = res.value.days[0]?.slots ?? [];
    if (slots.length === 0) continue;
    for (let guard = 0; guard < 200; guard++) {
      const again = await services.app.getSlots({ businessId: business.id, serviceId: service.id, staffId: 'any', fromDay: day, toDay: day });
      const next = again.ok ? again.value.days[0]?.slots[0] : undefined;
      if (!next) break;
      const token = Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), (b) => (b % 36).toString(36)).join('');
      const draft = {
        businessId: business.id, clientId: client.id, serviceId: service.id, staffId: next.staffId, start: next.start, source: 'owner' as const,
        status: 'confirmed' as Appointment['status'], manageToken: token, priceAgorot: service.priceAgorot, depositAgorot: 0, depositStatus: 'none' as const,
      };
      const r = await services.storage.bookAppointment(draft, services.clock.nowIso());
      if (!r.ok) break;
      booked++;
    }
    break;
  }
  return booked;
}

async function countMessages(): Promise<number> {
  const rows = await services.storage.repo('messages').list();
  return rows.filter((m) => m.status === 'sent' || m.status === 'delivered' || m.status === 'queued').length;
}
async function countExpiredDeposits(): Promise<number> {
  const rows = await services.storage.repo('appointments').list();
  return rows.filter((a) => a.cancelReason === 'deposit_timeout').length;
}

export interface JumpReport { when: string; sent: number; expired: number }
/** Moves the demo clock and reports what the schedulers did (reminders sent, deposits expired). */
export async function jumpClock(ms: number | { to: number }): Promise<JumpReport> {
  const before = await countMessages();
  const exp0 = await countExpiredDeposits();
  if (typeof ms === 'number') services.demo.advance(ms); else services.demo.setNow(ms.to);
  await services.scheduler.run().catch(() => undefined);
  await new Promise((r) => setTimeout(r, 60));
  await services.scheduler.run().catch(() => undefined);
  const sent = Math.max(0, (await countMessages()) - before);
  const expired = Math.max(0, (await countExpiredDeposits()) - exp0);
  return { when: services.clock.nowIso(), sent, expired };
}

/** Earliest queued message in the future (ms), or the next 24h step when none is waiting. */
export async function nextReminderAt(): Promise<number> {
  const now = services.clock.now();
  const rows = await services.storage.repo('messages').list({ where: { status: 'queued' } });
  const future = rows.map((m) => toMs(m.scheduledFor)).filter((x) => x > now).sort((a, b) => a - b);
  return (future[0] ?? now + DAY) + 60_000;
}
export { HOUR, atMinuteOfDay, toIso, newId };
